import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, BookOpen, Users, Gamepad2, FileText, Library, BookMarked, LayoutDashboard, Mail, Crown, CreditCard, Image } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL with cache busting
const getFullLogoUrl = (url) => {
  if (!url) return null;
  let fullUrl = url;
  if (url.startsWith('/uploads')) {
    fullUrl = `${API}/api${url}`;
  } else if (url.startsWith('/')) {
    fullUrl = `${API}${url}`;
  } else if (!url.startsWith('http')) {
    fullUrl = `${API}/${url}`;
  }
  // Add cache busting
  const separator = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${separator}v=${Date.now()}`;
};

// Default MedStudyPro Logo Component
const DefaultLogo = () => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="8" fill="#192A3F"/>
      <path d="M8 28V12L14 20L20 12L26 20L32 12V28" stroke="#7DD3FC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 20V28" stroke="#7DD3FC" strokeWidth="3" strokeLinecap="round"/>
      <path d="M26 20V28" stroke="#7DD3FC" strokeWidth="3" strokeLinecap="round"/>
    </svg>
    <span className="text-xl font-bold text-[#1E3A5C]">MedStudyPro</span>
  </div>
);

// Brand Logo with uploaded image
const BrandLogo = ({ logoUrl }) => (
  <div className="flex items-center gap-2">
    <img 
      src={logoUrl} 
      alt="MedStudyPro" 
      className="h-8 w-8 object-contain"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
    <span className="text-xl font-bold text-[#1E3A5C]">MedStudyPro</span>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [logoSettings, setLogoSettings] = useState({ logo_url: null, logo_light_url: null });

  useEffect(() => {
    fetchLogoSettings();
  }, []);

  const fetchLogoSettings = async () => {
    try {
      const res = await fetch(`${API}/api/logo-settings`);
      if (res.ok) {
        const data = await res.json();
        setLogoSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch logo settings:', err);
    }
  };

  const adminCards = [
    {
      id: 'questions',
      icon: BookOpen,
      iconBg: 'bg-[#FF9E00]/10',
      iconColor: 'text-[#FF9E00]',
      hoverBorder: 'hover:border-[#FF9E00]/50',
      hoverText: 'group-hover:text-[#FF9E00]',
      title: 'Manage Questions',
      description: 'Create, edit, and organize quiz questions across all clinical topics.',
      path: '/admin/questions'
    },
    {
      id: 'games',
      icon: Gamepad2,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      hoverBorder: 'hover:border-purple-500/50',
      hoverText: 'group-hover:text-purple-600',
      title: 'Game Questions',
      description: 'Upload and manage game-specific questions for Matching, Word Search, and Flashcards.',
      path: '/admin/games'
    },
    {
      id: 'case-studies',
      icon: FileText,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      hoverBorder: 'hover:border-blue-500/50',
      hoverText: 'group-hover:text-blue-600',
      title: 'Case Studies',
      description: 'Create and manage multi-stage clinical case studies with scoring and debriefs.',
      path: '/admin/case-studies'
    },
    {
      id: 'reference-sheets',
      icon: Library,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      hoverBorder: 'hover:border-teal-500/50',
      hoverText: 'group-hover:text-teal-600',
      title: 'Reference Sheets',
      description: 'Upload and manage reference images and PDFs for learners to study.',
      path: '/admin/reference-sheets'
    },
    {
      id: 'study-guides',
      icon: BookMarked,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      hoverBorder: 'hover:border-amber-500/50',
      hoverText: 'group-hover:text-amber-600',
      title: 'Shop',
      description: 'Add Amazon links to recommended study guides and resources.',
      path: '/admin/study-guides'
    },
    {
      id: 'tiers',
      icon: Crown,
      iconBg: 'bg-[#FF9E00]/10',
      iconColor: 'text-[#FF9E00]',
      hoverBorder: 'hover:border-[#FF9E00]/50',
      hoverText: 'group-hover:text-[#FF9E00]',
      title: 'Membership Tiers',
      description: 'Configure subscription tiers, pricing, and feature access.',
      path: '/admin/tiers'
    },
    {
      id: 'subscriptions',
      icon: CreditCard,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      hoverBorder: 'hover:border-green-500/50',
      hoverText: 'group-hover:text-green-600',
      title: 'User Subscriptions',
      description: 'View and manage user subscriptions, grant access, override tiers.',
      path: '/admin/subscriptions'
    },
    {
      id: 'about',
      icon: LayoutDashboard,
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      hoverBorder: 'hover:border-slate-500/50',
      hoverText: 'group-hover:text-slate-600',
      title: 'Edit About Page',
      description: 'Update the About page content, features, and benefits text.',
      path: '/admin/pages/about'
    },
    {
      id: 'contact',
      icon: Mail,
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      hoverBorder: 'hover:border-cyan-500/50',
      hoverText: 'group-hover:text-cyan-600',
      title: 'Edit Contact Page',
      description: 'Configure contact form, recipient email, and subject options.',
      path: '/admin/pages/contact'
    },
    {
      id: 'contact-submissions',
      icon: Mail,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      hoverBorder: 'hover:border-teal-500/50',
      hoverText: 'group-hover:text-teal-600',
      title: 'Contact Submissions',
      description: 'View and manage messages submitted through the contact form.',
      path: '/admin/contact-submissions'
    },
    {
      id: 'logo',
      icon: Image,
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      hoverBorder: 'hover:border-pink-500/50',
      hoverText: 'group-hover:text-pink-600',
      title: 'Logo Settings',
      description: 'Upload and manage the MedStudyPro logo for light and dark backgrounds.',
      path: '/admin/logo-settings'
    },
    {
      id: 'hero',
      icon: Image,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      hoverBorder: 'hover:border-indigo-500/50',
      hoverText: 'group-hover:text-indigo-600',
      title: 'Hero Image Settings',
      description: 'Upload and manage the landing page hero image.',
      path: '/admin/hero-settings'
    },
    {
      id: 'quiz-example',
      icon: Image,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      hoverBorder: 'hover:border-emerald-500/50',
      hoverText: 'group-hover:text-emerald-600',
      title: 'Quiz Example Image',
      description: 'Upload the quiz example image shown on the Browse Topics page.',
      path: '/admin/quiz-example-image'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3]">
      {/* Header */}
      <div className="bg-white border-b border-[#E8EEF3]">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Button
            data-testid="admin-back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#4A5E75] hover:text-[#1E3A5C] hover:bg-[#E8EEF3] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#192A3F] rounded-xl">
                <Shield className="w-6 h-6 text-[#FF9E00]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1E3A5C]">Admin Panel</h1>
                <p className="text-[#4A5E75] text-sm">Manage your platform content and settings</p>
              </div>
            </div>
            {/* Use uploaded logo or default - Admin has light background, use logo_light_url if available */}
            {logoSettings.logo_light_url || logoSettings.logo_url ? (
              <BrandLogo logoUrl={getFullLogoUrl(logoSettings.logo_light_url || logoSettings.logo_url)} />
            ) : (
              <DefaultLogo />
            )}
          </div>
        </div>
      </div>

      {/* Admin Cards */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                data-testid={`admin-${card.id}-card`}
                onClick={() => navigate(card.path)}
                className={`bg-white rounded-2xl border border-[#E8EEF3] p-6 hover:shadow-xl transition-all cursor-pointer group ${card.hoverBorder}`}
              >
                <div className={`${card.iconBg} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${card.iconColor}`} />
                </div>
                <h3 className={`text-lg font-semibold text-[#1E3A5C] mb-2 ${card.hoverText} transition-colors`}>
                  {card.title}
                </h3>
                <p className="text-[#4A5E75] text-sm leading-relaxed">
                  {card.description}
                </p>
              </div>
            );
          })}
          
          {/* Coming Soon Card */}
          <div className="bg-white/50 rounded-2xl border border-[#E8EEF3] p-6 opacity-60">
            <div className="bg-slate-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#1E3A5C] mb-2">User Management</h3>
            <p className="text-[#4A5E75] text-sm leading-relaxed">
              Coming soon: Manage user accounts and permissions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
